import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import axios from "axios";
import api from "../../api";
import { useLanguage } from "../../context/LanguageContext";

interface RecentArtwork {
  id: number;
  title_en: string;
  title_ro: string;
  catalogue_number: string | null;
  created_at?: string;
  updated_at?: string;
}

interface DashboardStats {
  totalArtworks: number;
  availableArtworks: number;
  soldArtworks: number;
  featuredArtworks: number;
  mostRecentlyAdded: RecentArtwork | null;
  mostRecentlyEdited: RecentArtwork | null;
}

const emptyStats: DashboardStats = {
  totalArtworks: 0,
  availableArtworks: 0,
  soldArtworks: 0,
  featuredArtworks: 0,
  mostRecentlyAdded: null,
  mostRecentlyEdited: null,
};

function DashboardOverview() {
  const { language } = useLanguage();

  const [stats, setStats] = useState<DashboardStats>(emptyStats);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const token = localStorage.getItem("token");

        if (!token) {
          throw new Error("No authentication token found");
        }

        const response = await api.get<DashboardStats>("/admin/dashboard", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        setStats(response.data);
        setError("");
      } catch (error) {
        console.error("Error fetching dashboard statistics:", error);

        if (axios.isAxiosError(error)) {
          if (error.response?.status === 401) {
            setError("Your session has expired. Please log in again.");
          } else {
            setError(`Failed to load dashboard: ${error.message}`);
          }
        } else if (error instanceof Error) {
          setError(error.message);
        } else {
          setError("Failed to load dashboard.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const formatDate = (date?: string) => {
    if (!date) {
      return "Date unavailable";
    }

    return new Intl.DateTimeFormat(language === "ro" ? "ro-RO" : "en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(new Date(date));
  };

  const getArtworkTitle = (artwork: RecentArtwork | null) => {
    if (!artwork) {
      return "No artwork found";
    }

    return language === "ro" ? artwork.title_ro : artwork.title_en;
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Dashboard
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6} sx={{ display: "flex" }}>
          <StatCard label="Total artworks" value={stats.totalArtworks} />
        </Grid>

        <Grid item xs={12} md={6} sx={{ display: "flex" }}>
          <StatCard label="Available" value={stats.availableArtworks} />
        </Grid>

        <Grid item xs={12} md={6} sx={{ display: "flex" }}>
          <StatCard label="Sold" value={stats.soldArtworks} />
        </Grid>

        <Grid item xs={12} md={6} sx={{ display: "flex" }}>
          <StatCard label="Featured" value={stats.featuredArtworks} />
        </Grid>

        <Grid item xs={12} md={6}>
          <RecentArtworkCard
            heading="Most recently added artwork"
            title={getArtworkTitle(stats.mostRecentlyAdded)}
            catalogueNumber={stats.mostRecentlyAdded?.catalogue_number}
            date={formatDate(stats.mostRecentlyAdded?.created_at)}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <RecentArtworkCard
            heading="Most recently edited artwork"
            title={getArtworkTitle(stats.mostRecentlyEdited)}
            catalogueNumber={stats.mostRecentlyEdited?.catalogue_number}
            date={formatDate(stats.mostRecentlyEdited?.updated_at)}
          />
        </Grid>
      </Grid>
    </Box>
  );
}

interface StatCardProps {
  label: string;
  value: number;
}

function StatCard({ label, value }: StatCardProps) {
  return (
    <Paper sx={{ p: 3, flex: 1 }}>
      <Typography
        variant="h6"
        fontWeight={600}
        color="text.primary"
        gutterBottom
      >
        {label}
      </Typography>

      <Typography variant="h3">{value}</Typography>
    </Paper>
  );
}

interface RecentArtworkCardProps {
  heading: string;
  title: string;
  catalogueNumber?: string | null;
  date: string;
}

function RecentArtworkCard({
  heading,
  title,
  catalogueNumber,
  date,
}: RecentArtworkCardProps) {
  return (
    <Paper sx={{ p: 3, flex: 1 }}>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        {heading}
      </Typography>

      <Typography variant="h6">{title}</Typography>

      {catalogueNumber && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Catalogue number: {catalogueNumber}
        </Typography>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {date}
      </Typography>
    </Paper>
  );
}

export default DashboardOverview;
